import {
  Badge,
  Button,
  Card,
  confirm,
} from "@devhop/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, PencilIcon, TrashIcon } from "lucide-react";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import {
  formFieldTypeIcon,
  formFieldTypeLabel,
  formFieldTypeTone,
} from "@/modules/forms/presentation/ui/builder/form-field-type-meta";

function fieldRowDescriptionPreview(field: FormFieldDTO): string {
  const d = field.description;
  if (!d) return "—";
  if (field.type === "section") {
    const text = d
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.length ? text : "—";
  }
  return d;
}

type FormSortableFieldRowProps = {
  field: FormFieldDTO;
  index: number;
  disabled?: boolean;
  canRemove: boolean;
  onEdit: (field: FormFieldDTO) => void;
  onRemove: (fieldId: string) => void;
};

export function FormSortableFieldRow({
  field,
  index,
  disabled,
  canRemove,
  onEdit,
  onRemove,
}: FormSortableFieldRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const tone = formFieldTypeTone(field.type);
  const Icon = formFieldTypeIcon(field.type);
  const isSection = field.type === "section";

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`group relative max-w-full overflow-hidden py-0 transition-shadow ${isDragging ? "shadow-lg ring-2 ring-primary/20" : "hover:shadow-sm"} ${isSection ? "border-dashed bg-muted/20" : ""}`}
    >
      <div className="flex min-w-0 items-stretch">
        <button
          type="button"
          className="flex w-full min-w-0 flex-1 items-start gap-3 p-4 text-left"
          onClick={() => onEdit(field)}
        >
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${tone.badge}`}
          >
            <Icon className={`h-4 w-4 ${tone.icon}`} />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="font-medium text-muted-foreground text-xs tabular-nums">
                #{index + 1}
              </span>
              <span className="min-w-0 break-words font-medium text-base leading-snug">
                {field.label}
              </span>
              <Badge
                variant="outline"
                className={`shrink-0 border-0 font-normal ${tone.badge}`}
              >
                {formFieldTypeLabel(field.type)}
              </Badge>
              {field.required ? (
                <Badge variant="secondary" className="shrink-0">
                  ຈໍາເປັນ
                </Badge>
              ) : null}
            </div>
            {!isSection ? (
              <p className="line-clamp-2 min-w-0 break-words text-muted-foreground text-sm [overflow-wrap:anywhere]">
                {fieldRowDescriptionPreview(field)}
              </p>
            ) : null}
          </div>
        </button>

        <div className="flex shrink-0 flex-col items-center justify-center gap-1 border-l px-2 py-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="ແກ້ໄຂ"
            onClick={() => onEdit(field)}
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          {canRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              aria-label="ລຶບ"
              onClick={async () => {
                const ok = await confirm({
                  title: "ລຶບຄຳຖາມນີ້",
                  description: "ຟິວບໍ່ສາມາດກູ້ຄືນ.",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onRemove(field.id);
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 cursor-grab touch-none"
            disabled={disabled}
            aria-label="ຈັດລຳດັບ"
            onClick={(e) => e.stopPropagation()}
            {...listeners}
            {...attributes}
          >
            <GripVerticalIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
